import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'

/**
 * Plan 261 — the customer is told when this tab has stopped saving.
 *
 * A refused save, or a load that never arrived, suspends the autosave (see
 * the store's subscriber). Suspending it without saying so would be worse
 * than the overwrite it prevents: the customer keeps typing into a tab that
 * persists nothing and looks completely normal.
 *
 * Two reasons, two sentences, because they are not the same problem.
 * `conflict` means somebody else changed the setup and this tab's basis is
 * stale. `load-failed` means the record never loaded, so what is on screen
 * may be defaults and writing them back would replace a real setup with an
 * empty one.
 *
 * Recovery is always "get the current version", never "merge". Rebasing a
 * customer's half-finished answers onto somebody else's is not a decision
 * this code is entitled to make. `onRecover` lets a page that cannot survive
 * a full page reload — the handoff page, whose reference is single-use —
 * reload just the record instead.
 */
export const SaveConflictBanner = ({
   onRecover,
   recoverLabel = 'Reload',
}: {
   onRecover?: () => void
   recoverLabel?: string
} = {}) => {
   const blocked = useOnboardingStoreV2((s) => s.onboardingSaveBlocked)
   if (!blocked) return null
   return (
      <div
         role="alert"
         className="w-full rounded-[12px] border border-[#FF8904]/40 bg-[#FF8904]/10 px-5 py-3 text-sm text-[#FFD9A8]"
      >
         <strong className="text-white">This tab has stopped saving.</strong>{' '}
         {blocked === 'conflict'
            ? 'Your setup was changed somewhere else — another tab, or your assistant — so the changes you have made here have not been stored.'
            : 'Your setup could not be loaded, so nothing you change here is being stored. What you see may not be your saved answers.'}{' '}
         Get the current version, then make your changes again.
         <button
            type="button"
            className="ml-3 rounded bg-[#9f7bff] px-3 py-1 text-xs text-white"
            onClick={() => (onRecover ? onRecover() : window.location.reload())}
         >
            {recoverLabel}
         </button>
      </div>
   )
}
